import type { FieldTemplateProps } from '@rjsf/utils';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import {
  isCheckboxes,
  isContributions,
  isCurrencyField,
  isMultiFileField,
  isPricing,
} from '../utils';
import CheckboxesWidget from '../widgets/checkboxes-widget';
import ContributionsWidget from '../widgets/contributions-widget';
import CurrencyWidget from '../widgets/currency-widget';
import DetailedFileUploaderWidget from '../widgets/detailed-file-uploader-widget';
import FileUploaderWidget from '../widgets/file-uploader-widget';
import ImageCropperWidget from '../widgets/image-cropper-widget';
import PricingWidget from '../widgets/pricing-widget';

/**
 * Simple wrapper for form fields – normally this component would render labels
 * and errors, but we are letting our input components (widgets) render them instead
 */
const FieldTemplate = (props: FieldTemplateProps) => {
  const theme = useTheme();
  const {
    id,
    schema,
    children,
    formData,
    registry,
    uiSchema,
    onChange,
    fieldPathId: { path: fieldPath },
  } = props;
  const formContext = registry?.formContext as
    | {
        hasDividerBetweenFields?: boolean;
        showFields?: string[];
      }
    | undefined;

  // Create a wrapped onChange that passes the field path
  // This is used by compound widgets that need the path parameter
  const onChangeWithPath = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => (value: any) => onChange(value, fieldPath),
    [onChange, fieldPath]
  );

  // Show two fields side by side
  const halfColumn = schema.properties?.['halfColumn'] === true;
  const style = {
    ...props.style,
    width: halfColumn ? `calc((100% - ${theme.spacing(4)}) / 2)` : '100%',
  };

  // Show dividers between fields
  const hasDividers = formContext?.hasDividerBetweenFields ?? false;
  const classNames = `${props.classNames} ${hasDividers && !halfColumn ? 'dividers' : ''}`;

  // Remove the root prefix to get the field key
  const key = id.replace('root_', '');

  // Hide when inner `hidden` property is set to true
  if (schema.properties?.['hidden'] === true) {
    return false;
  }

  // Hide when field does not appear in `showFields` form option
  if (
    id !== 'root' &&
    formContext?.showFields &&
    !formContext.showFields.includes(key)
  ) {
    return false;
  }

  // Render a currency widget if schema is an object with `amount` and `currency`
  if (isCurrencyField(schema)) {
    return (
      <div className={classNames} style={style}>
        <CurrencyWidget {...props} onChange={onChangeWithPath} />
      </div>
    );
  }

  // Render checkboxes widget if schema is an array with format 'checkboxes'
  if (isCheckboxes(schema)) {
    return (
      <div className={classNames} style={style}>
        <CheckboxesWidget
          {...props}
          value={formData}
          label={schema.title ?? ''}
          onChange={onChangeWithPath}
        />
      </div>
    );
  }

  /**
   * Render image cropper widget if form field `croppableImage` is true
   */
  if (uiSchema?.croppableImage) {
    return (
      <div className={classNames} style={style}>
        <ImageCropperWidget
          {...props}
          value={formData}
          label={schema.title ?? ''}
          onChange={onChangeWithPath}
        />
      </div>
    );
  }
  // Render a file upload widget if schema is an array with `data-url` item format
  if (isMultiFileField(schema)) {
    const schemaItems = schema.items as { type: string } | undefined;
    if (schemaItems?.type === 'object') {
      return (
        <div className={classNames} style={style}>
          <DetailedFileUploaderWidget
            {...props}
            value={formData}
            label={schema.title ?? ''}
            onChange={onChangeWithPath}
          />
        </div>
      );
    }
    return (
      <div className={classNames} style={style}>
        <FileUploaderWidget
          {...props}
          value={formData}
          label={schema.title ?? ''}
          onChange={onChangeWithPath}
        />
      </div>
    );
  }

  // Render a pricing widget if schema is an object with `pricing` item format
  if (isPricing(schema)) {
    return (
      <div className={classNames} style={style}>
        <PricingWidget
          {...props}
          value={formData}
          onChange={onChangeWithPath}
        />
      </div>
    );
  }

  // Render a contributions widget if schema is an object with `contributions` format
  if (isContributions(schema)) {
    return (
      <div className={classNames} style={style}>
        <ContributionsWidget
          {...props}
          value={formData}
          onChange={onChangeWithPath}
        />
      </div>
    );
  }

  return (
    <div className={classNames} style={style}>
      {children}
    </div>
  );
};

export default FieldTemplate;
